import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';
import { useAuth } from '@/hooks/useAuth';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import {
  LayoutDashboard,
  Building2,
  FileText,
  BarChart3,
  User,
  Menu,
  LogOut,
  Shield,
  Users,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface DashboardLayoutProps {
  children: React.ReactNode;
  title?: string;
}

const navItems = [
  {
    label: 'Dashboard',
    path: ROUTE_PATHS.DASHBOARD,
    icon: LayoutDashboard,
  },
  {
    label: 'Condomini',
    path: ROUTE_PATHS.CONDOMINI,
    icon: Building2,
  },
  {
    label: 'Documenti',
    path: ROUTE_PATHS.DOCUMENTI,
    icon: FileText,
  },
  {
    label: 'Analisi',
    path: ROUTE_PATHS.ANALISI,
    icon: BarChart3,
  },
  {
    label: 'Portale Condomini',
    path: ROUTE_PATHS.PORTALE_CONDOMINI,
    icon: Users,
  },
];

const SidebarContent = ({ onItemClick }: { onItemClick?: () => void }) => {
  const { profile, signOut, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate(ROUTE_PATHS.LOGIN);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center justify-center border-b border-sidebar-border px-6">
        <div className="flex items-center gap-2">
          <img
            src={IMAGES.LOGO_EGIM_25}
            alt="EGIM Logo"
            className="h-10 w-auto object-contain"
          />
          <span className="text-xl font-bold text-sidebar-primary">EGIM SM</span>
        </div>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-4">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onItemClick}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-primary'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-primary'
                }`
              }
            >
              <Icon className="h-5 w-5" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}

        {isSuperAdmin && (
          <>
            <div className="mx-3 my-2 border-t border-sidebar-border" />
            <NavLink
              to={ROUTE_PATHS.ADMIN}
              onClick={onItemClick}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-primary'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-primary'
                }`
              }
            >
              <Shield className="h-5 w-5" />
              <span>Admin</span>
            </NavLink>
          </>
        )}

        <div className="mx-3 my-2 border-t border-sidebar-border" />
        <NavLink
          to={ROUTE_PATHS.PROFILE}
          onClick={onItemClick}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
              isActive
                ? 'bg-sidebar-accent text-sidebar-primary'
                : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-primary'
            }`
          }
        >
          <User className="h-5 w-5" />
          <span>Profilo</span>
        </NavLink>
      </nav>

      <div className="border-t border-sidebar-border p-4">
        <div className="flex items-center gap-3 rounded-lg bg-sidebar-accent p-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground">
              {profile?.full_name ? getInitials(profile.full_name) : 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 overflow-hidden">
            <p className="truncate text-sm font-medium text-sidebar-foreground">
              {profile?.full_name || 'Utente'}
            </p>
            <p className="truncate text-xs text-sidebar-foreground/70">
              {profile?.email || ''}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="h-8 w-8 text-sidebar-foreground hover:text-destructive"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export function DashboardLayout({ children, title }: DashboardLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const location = useLocation();
  const { profile } = useAuth();

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
      }
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const getPageTitle = () => {
    if (title) return title;
    const path = location.pathname;
    if (path === ROUTE_PATHS.DASHBOARD) return 'Dashboard';
    if (path === ROUTE_PATHS.CONDOMINI) return 'Condomini';
    if (/^\/condomini\/.+\/dashboard/.test(path)) return 'Dashboard Condominio';
    if (path === ROUTE_PATHS.DOCUMENTI) return 'Documenti';
    if (path === ROUTE_PATHS.ANALISI) return 'Analisi';
    if (path.startsWith('/analisi/') && path.endsWith('/report')) return 'Report PDF';
    if (path.startsWith('/analisi/')) return 'Dettaglio Analisi';
    if (path === ROUTE_PATHS.PROFILE) return 'Profilo';
    if (path === ROUTE_PATHS.ADMIN) return 'Amministrazione';
    if (path === ROUTE_PATHS.PORTALE_CONDOMINI) return 'Portale Condomini';
    return 'Dashboard';
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <aside className="hidden w-60 flex-shrink-0 border-r border-sidebar-border bg-sidebar lg:block">
        <SidebarContent />
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-60 border-sidebar-border bg-sidebar p-0">
          <SidebarContent onItemClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header
          ref={headerRef}
          className="flex h-16 items-center justify-between border-b border-border bg-card px-4 lg:px-6"
        >
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-semibold text-foreground">{getPageTitle()}</h1>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {profile?.full_name || 'Utente'}
            </span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-muted/30">
          <div className="p-4 lg:p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>
    </div>
  );
}